/**
 * ExplorePage — the regular-user marketplace surface.
 *
 * Three stacked galleries:
 *   1. The 15-card Municipal Mirror suite at the top.
 *   2. The 24-segment grid (ready first, then by tier).
 *   3. The Marketplace listings — every published GI from
 *      `GET /api/v1/marketplace/listings`. Replaces the old
 *      `/marketplace` "All Listings" tab so users who click
 *      "Explore" still see the products they can install.
 *
 * Galleries 1 and 2 cascade in via the motion library's `stagger`
 * + `arrive` verbs. The listings gallery uses the same breakpoints
 * as the rest of the page and reuses `MarketCard` from the legacy
 * marketplace surface — clicking a card opens the existing
 * `/marketplace/:id` detail page where the Install button lives,
 * matching the legacy install flow exactly.
 *
 * A Cmd+K command palette is mounted at page-scope so the keyboard
 * shortcut works while Explore is mounted.
 *
 * Per [docs/DASHBOARD-HOME-EXPLORE-IMPLEMENTATION.md §3.6].
 */

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { Search } from 'lucide-react';

import { SEGMENTS, getSegment } from '@/data/segments';
import { SegmentTile } from '@/components/SegmentTile';
import { CommandPalette } from '@/components/CommandPalette';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { SkeletonGrid } from '@/components/LoadingState';
import { SegmentDetail } from '@/features/segments/SegmentDetail';
import { MarketCard } from '@/features/marketplace/MarketCard';
import { useMarketplaceList } from '@/hooks/useMarketplace';
import { stagger } from '@/lib/motion';

export function ExplorePage() {
  const { t } = useTranslation('marketplace');
  const reduce = useReducedMotion();
  const [searchParams, setSearchParams] = useSearchParams();
  const segFilter = searchParams.get('seg');
  const segRef = useRef<HTMLElement | null>(null);

  // Sort: ready segments first, then by tier.
  const orderedSegments = [...SEGMENTS].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'ready' ? -1 : 1;
    return a.tier - b.tier;
  });

  // When a segment filter is present, show SegmentDetail (same as
  // CategoriesView). This fixes the bug where clicking a domain card
  // appeared to "route to Citizen Mirror" because the user landed on
  // the full Explore page with Citizen Mirror as the first visible card.
  const selectedSegment = segFilter ? getSegment(segFilter) : undefined;

  const clearSegment = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('seg');
    setSearchParams(next, { replace: false });
  };

  // Scroll to the segments section when arriving without a ?seg= filter.
  useEffect(() => {
    if (!segFilter && segRef.current) {
      segRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [segFilter]);

  // Marketplace listings. When a segment filter is in the URL (?seg=),
  // pass the segment slug as the search query so the API returns
  // listings tagged with that segment. Otherwise fetch all 80.
  const listingsQuery = useMarketplaceList(
    segFilter ? { search: segFilter } : {},
  );

  // When a specific segment is selected, delegate to SegmentDetail
  // exactly as CategoriesView does — shows the 5 starters + connectors
  // for that domain with a back button to return to the full grid.
  if (selectedSegment) {
    return (
      <>
        <CommandPalette />
        <SegmentDetail segment={selectedSegment} onBack={clearSegment} />
      </>
    );
  }

  return (
    <>
      <CommandPalette />

      <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            {t('explore.title')}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            {t('explore.subtitle', {
              domainCount: SEGMENTS.length,
              recipeCount: SEGMENTS.reduce(
                (acc, s) => acc + s.starters.length,
                0,
              ),
            })}
          </p>
        </div>

        {/* Cmd+K hint (desktop) + Search button (mobile) */}
        <div className="flex items-center gap-2">
          <kbd className="hidden items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs text-ink-muted shadow-subtle sm:inline-flex">
            <span className="font-mono">⌘K</span>
            <span>{t('explore.search')}</span>
          </kbd>
          <button
            type="button"
            onClick={() => {
              // Synthesize a Cmd+K event so the palette opens.
              window.dispatchEvent(
                new KeyboardEvent('keydown', {
                  key: 'k',
                  metaKey: true,
                }),
              );
            }}
            aria-label={t('explore.search')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-surface text-ink-muted shadow-subtle hover:text-ink sm:hidden"
          >
            <Search className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </header>

      {/* ── 24-segment grid ────────────────────────────────────────── */}
      <motion.section
        ref={(el) => { segRef.current = el; }}
        aria-labelledby="segments-heading"
        {...(reduce ? {} : { variants: stagger, initial: 'hidden', animate: 'visible' })}
      >
        <div className="mb-3 flex items-baseline justify-between">
          <h2
            id="segments-heading"
            className="text-base font-semibold tracking-tight"
          >
            {t('explore.segments_title')}
          </h2>
          <p className="text-xs text-ink-muted">
            {t('explore.segments_subtitle')}
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {orderedSegments.map((s) => (
            <SegmentTile key={s.id} segment={s} />
          ))}
        </div>
      </motion.section>

      {/* ── Marketplace listings ──────────────────────────────────── */}
      {/* Restores the "All Listings" surface that lived under the old
          /marketplace Listings tab. Each card links to
          /marketplace/:id where the existing Install flow runs. The
          query layer caches via TanStack Query so revisiting Explore
          doesn't re-hit the API unless the cache is stale. */}
      <motion.section
        aria-labelledby="marketplace-heading"
        className="mt-12"
        {...(reduce ? {} : { variants: stagger, initial: 'hidden', animate: 'visible' })}
      >
        <div className="mb-3 flex items-baseline justify-between gap-4">
          <h2
            id="marketplace-heading"
            className="text-base font-semibold tracking-tight"
          >
            {t('explore.marketplace_title')}
          </h2>
          {listingsQuery.data && listingsQuery.data.length > 0 ? (
            <p className="text-xs text-ink-muted">
              {t('explore.marketplace_count', { count: listingsQuery.data.length })}
            </p>
          ) : (
            <p className="text-xs text-ink-muted">
              {t('explore.marketplace_subtitle')}
            </p>
          )}
        </div>

        {listingsQuery.isPending ? (
          <SkeletonGrid />
        ) : listingsQuery.isError ? (
          <ErrorState
            title={t('error.title')}
            body={t('error.body')}
            onRetry={() => void listingsQuery.refetch()}
          />
        ) : listingsQuery.data.length === 0 ? (
          <EmptyState
            title={t('empty.title')}
            body={t('empty.body')}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listingsQuery.data.map((l) => (
              <MarketCard key={l.root_id} listing={l} />
            ))}
          </div>
        )}
      </motion.section>
    </>
  );
}
